import { useTeams } from '@/domains/teams';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Loader2, Search } from 'lucide-react';
import { useState } from 'react';
import type { AssignShiftToTeamInput, TeamAssignmentResult } from '../types/shift';

interface AssignToTeamModalProps {
  open: boolean;
  shiftId: number;
  onClose: () => void;
  onAssign: (data: AssignShiftToTeamInput) => Promise<TeamAssignmentResult>;
}

export function AssignToTeamModal({ open, shiftId, onClose, onAssign }: AssignToTeamModalProps) {
  const [search, setSearch] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: teams = [], isLoading } = useTeams();

  // No team search endpoint, so filter client-side.
  const filteredTeams = teams.filter(
    (team) => !search || team.name?.toLowerCase().includes(search.toLowerCase()),
  );

  const reset = () => {
    setSelectedTeamId(null);
    setStartDate('');
    setEndDate('');
    setSearch('');
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedTeamId || !startDate || !endDate) return;
    setIsSubmitting(true);
    try {
      // The hook owns the toast from the per-member summary, so don't claim success here.
      const result = await onAssign({ shiftId, teamId: selectedTeamId, startDate, endDate });
      // Keep the modal open when every member clashed (successful: 0) so the user can retry.
      if (!result || result.summary.successful > 0) {
        close();
      }
    } catch {
      // Errors are surfaced by the mutation's global error handler.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) close();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign Shift to Team</DialogTitle>
          <DialogDescription>Creates assignments for all team members</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground mb-1.5 block">
              Select Team *
            </label>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search teams..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1 border border-border rounded-lg p-2">
              {isLoading ? (
                <div className="text-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mt-2">Loading teams...</p>
                </div>
              ) : filteredTeams.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  {search ? 'No teams match your search' : 'No teams found'}
                </div>
              ) : (
                filteredTeams.map((team) => (
                  <div
                    key={team.id}
                    className={`p-2 rounded cursor-pointer transition-all ${
                      selectedTeamId === team.id
                        ? 'bg-orange-50 dark:bg-orange-950/20 border border-orange-600'
                        : 'hover:bg-muted/50 border border-transparent'
                    }`}
                    onClick={() => setSelectedTeamId(team.id)}
                  >
                    <div className="font-medium text-sm text-foreground">{team.name}</div>
                    <div className="text-xs text-muted-foreground">{team.memberCount} members</div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">
                Start Date *
              </label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">End Date *</label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            className="bg-orange-600 hover:bg-orange-700 text-white"
            onClick={handleSubmit}
            disabled={!selectedTeamId || !startDate || !endDate || isSubmitting}
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Assign to Team
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
